// wiring.ts — 데이터·API 배선(개발자 소유). 컨덕터는 이 파일이 없을 때 한 번 만들고 다시 건드리지 않는다.
//  화면 파일(page.generated.tsx)은 컨덕터가 재생성 때 통째로 덮어쓴다 — 배선은 여기에만 둔다.
//  GI17 출고취소(비제스트) 1.0 → 2.0 (LOGISPRD-16035). 조회 전용. moms-be `/api/v1/bizest-cancel-results` 의 search·download 만 부른다.
//  1.0 의 저장·송신·엑셀 업로드는 운영에서 바꿀 행이 남지 않아 옮기지 않았다(moms-be LLD-outbound-gi17-bizest-cancel-screen 1장).
import { useEffect, useMemo, useSyncExternalStore } from 'react'
import dayjs from 'dayjs'
import type { Wiring } from './page.generated'
import { invalidError, failureError, notice } from '@/components/pbo/notice'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { bizestCancelResultQueryKeys, useGetBizestCancelResults } from '@/services/outbound/bizestCancelResult/useBizestCancelResultQueries'
import type {
  BizestCancelResultDownloadReq,
  BizestCancelResultRowDto,
  BizestCancelResultSearchReq,
} from '@/services/outbound/bizestCancelResult/types'
import {
  BIZEST_CANCEL_DEFAULT_TODAY_SHIPPER,
  BIZEST_CANCEL_REASON_CODES,
  DEFAULT_PERIOD_DAYS_OTHER_SHIPPER,
  MAX_PERIOD_DAYS,
  MAX_SEARCH_VALUES,
} from '@/services/outbound/bizestCancelResult/consts'
import { useGetShipperOptions } from '@/services/shipper/useShipperQueries'
import { type ShipperScope, shipperScopeOf } from '@/components/system/shipperScope'
import { buildShipperNameOptions } from '@/components/system/shipperSelectFilter'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { queryClient } from '@/utils/queryClient'
import { DOWNLOAD_PASSWORD_INVALID_MESSAGE, isValidDownloadPassword } from '@/utils/downloadPassword'

type Row = Record<string, unknown>

/** page.schema.json bcDownloadModal 상세사유 minLength 와 같은 값이어야 한다. 스키마를 바꾸면 여기도 바꾼다. */
const MIN_DETAIL_LENGTH = 5
const YN = (flag: boolean): 'Y' | 'N' => (flag ? 'Y' : 'N')
const text = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const list = (v: unknown): string[] =>
  (Array.isArray(v) ? v : [v])
    .map(text)
    .map((s) => s.trim())
    .filter(Boolean)
const yyyymmdd = (v: string) => v.replaceAll('-', '')

/** 사유코드 표시 — 1.0 GI17.js valueFormatter 와 같이 '코드 이름'. 목록에 없는 값은 원문 그대로. */
const REASON_LABEL = new Map<string, string>(BIZEST_CANCEL_REASON_CODES.map((o) => [o.value, o.label]))
export const reasonLabelOf = (code: string | null): string => (code ? REASON_LABEL.get(code) ?? code : '')

/** 행 키 — (화주, 주문번호, 주문일련번호)가 BE 의 행 식별자다. */
export const rowKeyOf = (shipperCode: string, orderNumber: string, orderOptionNumber: string) =>
  `${shipperCode}|${orderNumber}|${orderOptionNumber}`

export const toViewRow = (dto: BizestCancelResultRowDto): Row => ({
  rowKey: rowKeyOf(dto.shipperCode, dto.orderNumber, dto.orderOptionNumber),
  shipperCode: dto.shipperCode,
  clsDate: dto.clsDate ?? '',
  orderNumber: dto.orderNumber,
  orderOptionNumber: dto.orderOptionNumber,
  internalProcessed: YN(dto.internalProcessed),
  transmitResult: YN(dto.transmitResult),
  reasonCode: reasonLabelOf(dto.reasonCode),
  remark: dto.remark ?? '',
  insertedAt: dto.insertedAt ?? '',
})

/** 조회영역 값 → 조회 요청. 화주·기간이 비면 null(조회하지 않음). */
export const toSearchReq = (filters: Record<string, unknown>): BizestCancelResultSearchReq | null => {
  const shipperCode = text(filters.SPR_NM)
  const range = Array.isArray(filters.CLS_DATE) ? (filters.CLS_DATE as string[]) : []
  if (!shipperCode || !range[0] || !range[1]) return null
  const orderNumbers = list(filters.ORD_NO)
  const orderOptionNumbers = list(filters.ORD_OPT_NO)
  const transmitResult = text(filters.OIF_API_FLAG)
  const reasonCode = text(filters.RSN_CODE)
  return {
    shipperCode,
    clsDateFrom: yyyymmdd(range[0]),
    clsDateTo: yyyymmdd(range[1]),
    ...(orderNumbers.length ? { orderNumbers } : {}),
    ...(orderOptionNumbers.length ? { orderOptionNumbers } : {}),
    ...(transmitResult === 'Y' || transmitResult === 'N' ? { transmitResult } : {}),
    ...(reasonCode ? { reasonCode } : {}),
  }
}

// ── 화주 범위 ────────────────────────────────────────────────────────────────
// 화주 목록은 2.0 공용 화주 목록(전체 화주)이다(LOGISPRD-17357). 1.0 메타에는 MUSINSA·NIKE·NIKESAMPLE 만 있었지만
// IIF_BIZEST_CANCEL 에는 그 밖의 화주 행도 쌓인다(moms-be LLD 2장). 화주 계정은 자기 화주로 잠근다(2.0 공용 규칙 shipperScopeOf).
// 판정은 서버가 한다 — 허용 밖 화주는 빈 목록이다.
const scope = () => shipperScopeOf(AuthStorage.user.get())
const lockedShipperOption = (s: ShipperScope) => [{ value: s.shipperCode, label: s.shipperName || s.shipperCode }]
/** options 슬롯은 생성본 본문에서 매 렌더 같은 순서로 불린다 — 조회 훅을 써도 된다(wiring.skeleton.ts). */
const useShipperOptions = () => {
  const { data: shippers } = useGetShipperOptions()
  // 세션은 렌더마다 읽고 값으로 비교한다 — 화주 전환·이름 갱신이 목록 응답과 무관하게 반영된다.
  const { locked, shipperCode, shipperName } = scope()
  return useMemo(
    () =>
      locked ? lockedShipperOption({ locked, shipperCode, shipperName }) : buildShipperNameOptions(shippers ?? [], { valueKey: 'shipperCode' }),
    [shippers, locked, shipperCode, shipperName],
  )
}
/** 잠기지 않은 계정(FREE_SHIPPERS)은 1.0 처럼 MUSINSA 에서 시작한다. */
const defaultShipper = (): string => {
  const s = scope()
  return s.locked ? s.shipperCode : BIZEST_CANCEL_DEFAULT_TODAY_SHIPPER
}
/** URL 에서 되살릴 수 있는 화주 — 화주 계정은 자기 화주만, 그 밖은 아무 화주(범위 판정은 서버). */
const canRestoreShipper = (shipperCode: string): boolean => {
  const s = scope()
  return !s.locked || s.shipperCode === shipperCode
}
/** 1.0 systemTask.py:136 — MUSINSA 는 당일, 그 밖의 화주는 7일 전부터 오늘까지 */
export const defaultClsDate = (shipperCode: string, today = dayjs()): [string, string] => [
  (shipperCode === BIZEST_CANCEL_DEFAULT_TODAY_SHIPPER ? today : today.subtract(DEFAULT_PERIOD_DAYS_OTHER_SHIPPER, 'day')).format('YYYY-MM-DD'),
  today.format('YYYY-MM-DD'),
]

/** 1.0 공통 0건 문구(E15)와 같은 문구 */
export const SEARCH_EMPTY_MESSAGE = '조회된 결과가 존재하지 않습니다.'

/** 조회 요청 검증 — 막을 때 안내 문구, 통과하면 null. [조회] 와 URL 복원이 같은 기준을 쓴다. */
const invalidReasonOf = (req: BizestCancelResultSearchReq): string | null => {
  // 날짜 선택기는 뒤집힌 기간을 만들지 않는다. URL 을 손으로 고친 복원 경로만 여기 걸린다 — BE 는 400 이다.
  if (req.clsDateFrom > req.clsDateTo) return '취소요청일자 시작일이 종료일보다 늦습니다.'
  if (dayjs(req.clsDateTo, 'YYYYMMDD').diff(dayjs(req.clsDateFrom, 'YYYYMMDD'), 'day') >= MAX_PERIOD_DAYS) {
    return `취소요청일자는 최대 ${MAX_PERIOD_DAYS}일까지 조회할 수 있습니다.`
  }
  if ((req.orderNumbers?.length ?? 0) > MAX_SEARCH_VALUES || (req.orderOptionNumbers?.length ?? 0) > MAX_SEARCH_VALUES) {
    return `주문번호·주문일련번호는 각각 최대 ${MAX_SEARCH_VALUES}건까지 조회할 수 있습니다.`
  }
  return null
}

// ── URL·탭 세션 복원 ────────────────────────────────────────────────────────────
// 다른 탭에 다녀오면 화면이 다시 마운트된다. [조회] 로 확정한 조건을 URL 과 탭 세션(menuOnSession)에 싣고,
// 진입 때 URL 에서 되살려 같은 조회를 다시 건다. 탭을 닫으면 탭 세션이 지워져 다음 진입은 빈 화면이다.
// 실어 두는 모양은 조회 요청에서 목록을 뺀 것이다(날짜 YYYYMMDD). 목록은 아래 saveSearchLists 가 따로 둔다.
const DATE_PARAM = /^\d{8}$/
const restoreDate = (v: unknown): string | null => {
  const raw = text(v)
  return DATE_PARAM.test(raw) && dayjs(raw, 'YYYYMMDD').format('YYYYMMDD') === raw ? raw : null
}

/** URL 쿼리 → 조회 요청. 화주·기간이 없거나, 고를 수 없는 화주이거나, [조회] 가 막는 조건이면 null(복원하지 않음). */
export const fromSearchQuery = (query: Record<string, unknown>): BizestCancelResultSearchReq | null => {
  const shipperCode = text(query.shipperCode)
  const clsDateFrom = restoreDate(query.clsDateFrom)
  const clsDateTo = restoreDate(query.clsDateTo)
  if (!shipperCode || !clsDateFrom || !clsDateTo) return null
  if (!canRestoreShipper(shipperCode)) return null
  const orderNumbers = list(query.orderNumbers ?? [])
  const orderOptionNumbers = list(query.orderOptionNumbers ?? [])
  const transmitResult = text(query.transmitResult)
  const reasonCode = text(query.reasonCode)
  const req: BizestCancelResultSearchReq = {
    shipperCode,
    clsDateFrom,
    clsDateTo,
    ...(orderNumbers.length ? { orderNumbers } : {}),
    ...(orderOptionNumbers.length ? { orderOptionNumbers } : {}),
    ...(transmitResult === 'Y' || transmitResult === 'N' ? { transmitResult } : {}),
    ...(reasonCode ? { reasonCode } : {}),
  }
  return invalidReasonOf(req) ? null : req
}

/** 조회 요청 → 조회영역 값. toSearchReq 의 역이다. */
export const toFilters = (req: BizestCancelResultSearchReq): Record<string, unknown> => ({
  SPR_NM: req.shipperCode,
  CLS_DATE: [dayjs(req.clsDateFrom, 'YYYYMMDD').format('YYYY-MM-DD'), dayjs(req.clsDateTo, 'YYYYMMDD').format('YYYY-MM-DD')],
  ORD_NO: req.orderNumbers?.length ? [...req.orderNumbers] : undefined,
  ORD_OPT_NO: req.orderOptionNumbers?.length ? [...req.orderOptionNumbers] : undefined,
  OIF_API_FLAG: req.transmitResult ?? '',
  RSN_CODE: req.reasonCode ?? '',
})

// 주문번호·주문일련번호 목록은 URL 에 싣지 않는다. 1000건씩이면 쿼리만 수십 KB 라 새로고침 때 CloudFront URL 한도(8KB)를 넘는다
// (조회도 같은 이유로 POST 본문이다). 목록은 브라우저 탭의 sessionStorage 에 URL 조건과 함께 두고, 같은 조건으로 복원할 때만 붙인다.
export const SEARCH_LISTS_KEY = 'moms_gi40_search_lists'
type SavedLists = { key: string; orderNumbers?: string[]; orderOptionNumbers?: string[] }

/** URL·탭 세션에 싣는 조건 — 조회 요청에서 목록만 뺀다. */
export type BizestCancelUrlQuery = Omit<BizestCancelResultSearchReq, 'orderNumbers' | 'orderOptionNumbers'>
export const toUrlQuery = (req: BizestCancelResultSearchReq): BizestCancelUrlQuery => {
  const { orderNumbers: _orderNumbers, orderOptionNumbers: _orderOptionNumbers, ...rest } = req
  return rest
}

/** [조회] 로 확정한 목록을 URL 조건과 짝지어 둔다. 목록이 없거나 null 이면 지운다. 저장소를 못 쓰면 목록 없이 복원된다. */
export const saveSearchLists = (req: BizestCancelResultSearchReq | null) => {
  try {
    if (!req || (!req.orderNumbers?.length && !req.orderOptionNumbers?.length)) {
      sessionStorage.removeItem(SEARCH_LISTS_KEY)
      return
    }
    const saved: SavedLists = { key: JSON.stringify(toUrlQuery(req)), orderNumbers: req.orderNumbers, orderOptionNumbers: req.orderOptionNumbers }
    sessionStorage.setItem(SEARCH_LISTS_KEY, JSON.stringify(saved))
  } catch {
    // 저장소가 막힌 브라우저(사생활 보호 모드 등) — 목록 없이 복원한다
  }
}

/** URL 에서 되살린 조건에 같은 조건으로 저장해 둔 목록을 붙인다. 조건이 다르면(다른 조회의 목록) 붙이지 않는다. */
export const withSavedLists = (fromUrl: BizestCancelResultSearchReq | null): BizestCancelResultSearchReq | null => {
  if (!fromUrl) return null
  try {
    const saved = JSON.parse(sessionStorage.getItem(SEARCH_LISTS_KEY) ?? 'null') as SavedLists | null
    if (!saved || saved.key !== JSON.stringify(toUrlQuery(fromUrl))) return fromUrl
    // fromSearchQuery 로 다시 만들어 키 순서·검증(1000건 상한)을 [조회] 와 맞춘다
    return fromSearchQuery({ ...fromUrl, orderNumbers: saved.orderNumbers ?? [], orderOptionNumbers: saved.orderOptionNumbers ?? [] }) ?? fromUrl
  } catch {
    return fromUrl
  }
}

/** 조건을 URL·탭 세션에 싣는 함수 — 라우터 훅이 필요해 엔트리(index.tsx)가 묶는다. null 이면 비운다. */
type Persist = (req: BizestCancelResultSearchReq | null) => void
let persist: Persist | null = null
export const bindBizestCancelPersist = (fn: Persist | null) => {
  persist = fn
}

/** 엑셀 다운로드 함수 — useExcelDownload 훅이라 엔트리(index.tsx)가 묶는다. 실패 안내는 훅이 띄우고 resolve 한다. */
type Download = (body: BizestCancelResultDownloadReq) => Promise<void>
let download: Download | null = null
export const bindBizestCancelDownload = (fn: Download | null) => {
  download = fn
}

// ── 모듈 상태: [조회] 로 확정한 조건 ─────────────────────────────────────────────
// 표 슬롯은 입력 중인 조회영역 값을 받으므로, [조회] 를 누른 조건을 따로 들고 있어야 입력만으로 재조회되지 않는다.
// seq 는 [조회] 를 누른 횟수다. 0건 안내는 [조회] 로 연 조회(noticeSeq)에만 띄운다 — URL 복원 진입의 자동 조회는 누른 적 없는 안내가 된다.
type SearchState = { params: BizestCancelResultSearchReq | null; seq: number }
let state: SearchState = { params: null, seq: 0 }
let noticeSeq: number | null = null
/** 진입 때 복원한 조건 — 조회영역(filterInitial)에도 같은 값을 그린다. */
let restored: BizestCancelResultSearchReq | null = null
const listeners = new Set<() => void>()
const setState = (next: SearchState) => {
  state = next
  listeners.forEach((l) => l())
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}
/** 마운트 때 첫 렌더 전에 부른다. URL 에서 되살린 조건이 있으면 그 조건으로 조회하고, 없으면 빈 화면에서 시작한다(배선 체크 ⑤). */
export const initBizestCancelStore = (fromUrl: BizestCancelResultSearchReq | null) => {
  restored = fromUrl
  noticeSeq = null
  setState({ params: fromUrl, seq: 0 })
}
export const resetBizestCancelStore = () => initBizestCancelStore(null)

const refetch = () => queryClient.invalidateQueries({ queryKey: bizestCancelResultQueryKeys.all })

export const wiring: Wiring = {
  rows: {
    ROWS: () => {
      const s = useSyncExternalStore(subscribe, () => state)
      const { data, isFetching, status } = useGetBizestCancelResults(s.params)
      const rows = useMemo(() => (data ?? []).map(toViewRow), [data])
      // [조회] 로 연 조회가 끝났을 때 한 번만 본다. 실패 알럿은 전역(queryClient)이 띄우므로 실패면 조용히 지운다.
      // noticeSeq 는 렌더 상태가 아닌 모듈 변수라 deps 에 넣지 않는다 — 여기서 지워도 재렌더가 일어나지 않아야 안내가 한 번만 뜬다.
      useEffect(() => {
        if (s.seq === 0 || s.seq !== noticeSeq) return
        if (isFetching || status === 'pending') return
        noticeSeq = null
        if (status === 'success' && (data?.length ?? 0) === 0) notice.invalid(SEARCH_EMPTY_MESSAGE)
      }, [s.seq, data, isFetching, status])
      return { rows, total: rows.length, loading: isFetching }
    },
  },
  actions: {
    search: (filters) => {
      if (!text(filters.SPR_NM)) {
        notice.invalid('화주명을 선택해 주세요.')
        return false
      }
      const next = toSearchReq(filters)
      if (!next) {
        notice.invalid('취소요청일자를 입력해 주세요.')
        return false
      }
      const reason = invalidReasonOf(next)
      if (reason) {
        notice.invalid(reason)
        return false
      }
      // toSearchReq·fromSearchQuery 가 늘 같은 순서로 키를 만들어서 문자열 비교로 같은 조건인지 가린다.
      const same = JSON.stringify(next) === JSON.stringify(state.params)
      noticeSeq = state.seq + 1
      setState({ params: next, seq: noticeSeq })
      // 같은 조건 재조회는 queryKey 가 같아 요청이 나가지 않는다 — 무효화로 다시 받는다(배선 체크 ⑥).
      if (same) refetch().catch(() => undefined)
      persist?.(next)
      return undefined
    },
    /**
     * 엑셀 다운로드 — moms-be 가 [조회] 로 확정한 조건으로 다시 읽어 비밀번호 zip 으로 내려준다(LOGISPRD-17358).
     * 컬럼은 화면 표와 같다(BE 가 정한다, LOGISPRD-17359). 감사로그(사유)는 BE @AccessAudit 가 남긴다 — FE 감사 API 를 또 부르면 두 번 남는다.
     * 실패 안내는 useExcelDownload 가 띄우고 resolve 한다 — 성공·실패를 못 가르므로 성공 토스트 없이 모달을 닫는다(GI37·RGR05 와 같음).
     */
    bcDownloadModal_ok: async (ctx) => {
      const reason = text(ctx.form?.reason)
      const detail = text(ctx.form?.detail).trim()
      const password = text(ctx.form?.password)
      if (!reason) throw invalidError('사유코드를 선택해 주세요.')
      if (!detail) throw invalidError('상세사유를 입력해 주세요.')
      if (detail.length < MIN_DETAIL_LENGTH) throw invalidError(`상세사유는 ${MIN_DETAIL_LENGTH}글자 이상 입력해 주세요.`)
      if (!password) throw invalidError('파일 비밀번호를 입력해 주세요.')
      if (!isValidDownloadPassword(password)) throw invalidError(DOWNLOAD_PASSWORD_INVALID_MESSAGE)
      // 파일은 표가 아니라 [조회] 로 확정한 조건(state.params)으로 BE 가 다시 뽑는다. 표 위 목록 검색은 싣지 않는다 —
      // 1.0 도 목록 검색과 무관하게 조회한 행 전체를 내보냈다(ToolBar onBtnExport 의 gridApi.forEachNode).
      // 그래서 받을 행이 있는지도 목록 검색을 거친 ctx.visible 이 아니라 그 조건의 조회 결과로 본다.
      // 캐시 전제: 표 슬롯(ROWS)의 useGetBizestCancelResults 가 같은 키를 구독하는 동안은 GC 되지 않는다. 표를 조건부로 내리거나
      // gcTime 을 줄이면 이 판정이 '데이터 없음' 으로 바뀔 수 있다.
      const search = state.params
      const fetched = search ? queryClient.getQueryData<BizestCancelResultRowDto[]>(bizestCancelResultQueryKeys.list(search)) : undefined
      if (!search || !fetched?.length) throw invalidError('다운로드할 데이터가 없습니다. 조회 후 다운로드해 주세요.')
      if (!download) throw failureError('다운로드를 시작하지 못했습니다. 화면을 새로고침한 뒤 다시 시도해 주세요.')
      await download({ search, reasonCode: reason, detailedReason: detail, password })
      return undefined
    },
  },
  form: {
    bcDownloadModal: (form) => form,
  },
  options: {
    BCSHIPPER_OPTIONS: useShipperOptions,
    BCRSNCODE_OPTIONS: () => BIZEST_CANCEL_REASON_CODES.map((o) => ({ ...o })),
    // 다운로드 사유코드 — 콤보 DOWNLOAD_CD(2.0 공통)
    // 배선 계약: options 슬롯은 생성본 컴포넌트 본문에서 매 렌더 같은 순서로 불린다 — 안에서 조회 훅을 써도 된다(wiring.skeleton.ts).
    REASONRADIO_OPTIONS: (demo) => {
      const { data } = useGetExportCombo({ errorToast: true })
      const cds = data?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []
      const nms = data?.DOWNLOAD_CD?.COMBO_OPT_NM ?? []
      const options = cds.map((value, i) => ({ value, label: nms[i] ?? value })).filter((o) => o.value)
      return options.length ? options : demo
    },
  },
  // 생성본이 렌더마다 읽는다 — 세션(화주 전환)을 그때 본다.
  filterDisabled: {
    get SPR_NM() {
      return scope().locked
    },
  },
  filterDefaults: {
    bcFilterCard: () => {
      const shipperCode = defaultShipper()
      return { SPR_NM: shipperCode, CLS_DATE: defaultClsDate(shipperCode) }
    },
  },
  // 진입 때 URL 에서 되살린 조건을 조회영역에도 그린다. [초기화] 는 이 값이 아니라 filterDefaults 로 돌아간다.
  filterInitial: {
    bcFilterCard: (defaults) => (restored ? { ...defaults, ...toFilters(restored) } : defaults),
  },
  // [초기화] 는 조회영역만 기본값으로 돌린다. 실어 둔 조건도 지워 다음 진입이 초기화한 상태에서 시작하게 한다(일마감조회 선례).
  onReset: () => persist?.(null),
}
